import { Button, Divider, Stack, Typography } from '@mui/material'
import Box from '@mui/material/Box'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store'
import { File } from '../../models/Organization'
import { calculateAge } from '../../utils/Common'
import { createSelector } from '@reduxjs/toolkit'
import { updateSelectedFile } from '../../actions/organization'

const THUMBNAILS_URL = process.env.REACT_APP_THUMBNAILS_URL

interface FileCardProps {
  file: File
}

const clientsSelector = createSelector(
  [(state: RootState) => state.organization],
  (organization) => organization.clients,
)
const projectsSelector = createSelector(
  [(state: RootState) => state.organization],
  (organization) => organization.projects,
)

const FileCardComponent: React.FC<FileCardProps> = ({ file }) => {
  const dispatch = useDispatch<any>()
  const clients = useSelector(clientsSelector)
  const projects = useSelector(projectsSelector)
  const project = projects.find((project) => project.id === file.project_id)
  const client = clients.find((client) => client.id === project?.client_id)

  const handleFileClick = () => {
    dispatch(updateSelectedFile(file))
  }

  return (
    <Button
      onClick={handleFileClick}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
        p: '1rem',
        background: '#F5F5F1',
        boxShadow: '0px 2px 10px 0px #0000001A',
        borderRadius: '0.4rem',
        textTransform: 'none',
        textAlign: 'left',
        minHeight: '14rem',
      }}
    >
      <Stack gap='0.75rem' sx={{ width: '100%', height: '100%' }}>
        <Box
          gap={'15px'}
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              justifyContent: 'space-between',
            }}
          >
            <Box>
              <Stack gap='0.3rem'>
                <Typography
                  sx={{
                    color: '#04002E',
                    fontSize: '0.88rem',
                    fontWeight: 500,
                    lineHeight: 'normal',
                  }}
                >
                  {file.name}
                </Typography>
                <Typography
                  sx={{
                    color: '#878787',
                    fontSize: '0.82rem',
                    lineHeight: 'normal',
                  }}
                >
                  San Diego, CA
                </Typography>
                <Divider sx={{ background: '#A2B0C6' }} />
                <Typography
                  sx={{
                    color: '#878787',
                    fontSize: '0.76rem',
                    lineHeight: 'normal',
                  }}
                >
                  {client ? client.name : 'Client Name'}
                </Typography>
              </Stack>
            </Box>
            <Box>
              <Stack gap='0.3rem'>
                <Box
                  sx={{
                    display: 'flex',
                    gap: '10px',
                  }}
                >
                  <Typography
                    component='div'
                    sx={{
                      fontWeight: 400,
                      color: '#04002E',
                      fontSize: '0.7rem',
                    }}
                  >
                    3 BR
                  </Typography>
                  <Divider flexItem orientation='vertical' sx={{ my: '2px' }} />
                  <Typography
                    component='div'
                    sx={{
                      fontWeight: 400,
                      color: '#04002E',
                      fontSize: '0.7rem',
                    }}
                  >
                    2 BA
                  </Typography>
                  <Divider flexItem orientation='vertical' sx={{ my: '2px' }} />
                  <Typography
                    component='div'
                    sx={{
                      fontWeight: 400,
                      color: '#04002E',
                      fontSize: '0.7rem',
                    }}
                  >
                    1 FL
                  </Typography>
                </Box>
                <Divider sx={{ background: '#A2B0C6' }} />
                <Typography
                  sx={{
                    color: '#06004A',
                    fontSize: '0.64rem',
                    lineHeight: 'normal',
                  }}
                >
                  Edited:{' '}
                  {calculateAge(file.updatedAt ?? Date.now().toString())} ago
                </Typography>
              </Stack>
            </Box>
          </Box>
          <Box
            component='img'
            src={
              !!file.thumbnail_path
                ? `${THUMBNAILS_URL}/thumbnails/${file.thumbnail_path}`
                : '/images/fileImage.png'
            }
            alt='Project Thumbnail'
            sx={{
              width: '57%',
              borderRadius: '2px',
            }}
          ></Box>
        </Box>
      </Stack>
    </Button>
  )
}

export default FileCardComponent
